import {
  PRODUCT_NAME,
  ZOHO_REASON_PARAM,
  ZOHO_RETURN_CONNECTED,
  ZOHO_RETURN_ERROR,
  ZOHO_RETURN_PARAM,
  ZOHO_RETURN_REASONS,
  type ZohoReturnReason,
} from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { useWorkspaceId } from "../../../session/workspace-context";
import { wsKeys } from "../shared";

const P = "settings.integrations.zoho";
/** A reason this build does not know (a newer API) reads as the generic failure. */
const FALLBACK_REASON: ZohoReturnReason = "failed";

export type ZohoReturn = { kind: "connected" } | { kind: "error"; reason: ZohoReturnReason };

const isReason = (value: string | null): value is ZohoReturnReason => ZOHO_RETURN_REASONS.some((reason) => reason === value);

/** What the API's redirect back to Settings > Integrations said (`?zoho=connected` / `?zoho=error&reason=...`). */
export function parseZohoReturn(params: URLSearchParams): ZohoReturn | null {
  const result = params.get(ZOHO_RETURN_PARAM);
  if (result === ZOHO_RETURN_CONNECTED) return { kind: "connected" };
  if (result !== ZOHO_RETURN_ERROR) return null;
  const reason = params.get(ZOHO_REASON_PARAM);
  return { kind: "error", reason: isReason(reason) ? reason : FALLBACK_REASON };
}

/** The query string without the return flags (other parameters stay). */
export function withoutZohoReturn(params: URLSearchParams): URLSearchParams {
  const rest = new URLSearchParams(params);
  rest.delete(ZOHO_RETURN_PARAM);
  rest.delete(ZOHO_REASON_PARAM);
  return rest;
}

/**
 * Finishes the Zoho connect on the Integrations panel: toasts the outcome, refreshes the integrations
 * and Zoho status, and strips the flags from the URL (replace, so Back does not repeat the toast).
 */
export function useZohoReturn(): void {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();
  const wsId = useWorkspaceId();
  const announced = useRef(false);
  useEffect(() => {
    const outcome = parseZohoReturn(params);
    if (!outcome) {
      announced.current = false;
      return;
    }
    if (announced.current) return;
    announced.current = true;
    if (outcome.kind === "connected") toast.success(t(`${P}.returned.connected`));
    else if (outcome.reason === "denied") toast.info(t(`${P}.returned.reasons.denied`));
    else toast.error(t(`${P}.returned.reasons.${outcome.reason}`, { product: PRODUCT_NAME }));
    void client.invalidateQueries({ queryKey: wsKeys.integrations(wsId) });
    setParams(withoutZohoReturn(params), { replace: true });
  }, [params, setParams, client, wsId, t]);
}
